import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { productBrandsState, productCategoriesState, productDistributorsState } from "../atoms";
import { getBrandsAPI, getCategoriesAPI, getDistributorsAPI } from "../common/services";
import { deepClone } from "../utils";

/**
 * Hook to get categories, brands and distributors.
 *
 * All three are now served by REST under /apis/{brands,categories,distributors}.
 * Recoil-cache contract preserved: only fetch when the matching atom is empty.
 */
export default function useGetFacetsData() {
  const [productCategories, setProductCategories] = useRecoilState(productCategoriesState)
  const [productBrands, setProductBrands] = useRecoilState(productBrandsState)
  const [productDistributors, setProductDistributors] = useRecoilState(productDistributorsState)

  // ---REST fetch for brands
  useEffect(() => {
    if (productBrands.length) return;
    let cancelled = false;
    getBrandsAPI()
      .then((brands) => {
        if (!cancelled) setProductBrands(brands);
      })
      .catch(() => {
        // Swallow — matches the previous useQuery behavior of discarding errors.
      });
    return () => { cancelled = true; };
  }, [productBrands.length, setProductBrands]);

  // ---REST fetch for categories (sorted by displayName, matching prior order)
  useEffect(() => {
    if (productCategories.length) return;
    let cancelled = false;
    getCategoriesAPI()
      .then((categories) => {
        if (cancelled) return;
        const sorted = deepClone(categories).sort((a, b) =>
          a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0
        );
        setProductCategories(sorted);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productCategories.length, setProductCategories]);

  // ---REST fetch for distributors
  useEffect(() => {
    if (productDistributors.length) return;
    let cancelled = false;
    getDistributorsAPI()
      .then((distributors) => {
        if (!cancelled) setProductDistributors(distributors);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productDistributors.length, setProductDistributors]);

  return { categories: productCategories, brands: productBrands, distributors: productDistributors }
}
